import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { isMobile } from 'react-device-detect'
import { Helmet } from 'react-helmet'
import { CartItem, ConfigBrand, MenuItem } from '@open-tender/types'
import { makeUpsellItemsForCart, makeMenuItemLookup, makeOrderItem } from '@open-tender/utils'
import {
  selectBrand,
  selectCart,
  selectCartIds,
  selectContentSection,
  selectCustomer,
  selectMenu,
  selectMenuSlug,
  selectQuote,
  useAppSelector,
  selectConfig,
  selectRecommendations,
  fetchRecommendations,
  resetRecommendations,
  selectOrder,
  useAppDispatch,
} from '@open-tender/cloud'
import { Back, Skip } from 'components/buttons'
import {
  Button,
  ButtonLink,
  Content,
  Header,
  HeaderLogo,
  Main,
  UpsellItems,
} from 'components'
import {
  UpsellFooter,
  UpsellHeader,
  UpsellSubtitle,
  UpsellTitle,
  UpsellView,
} from './Upsell.styled'
import { sendUpsillsMenuView } from 'app/analytics'
import { makeUpsellsAnalyticsItems } from 'utils/helpers'

type ExtConfigBrand = ConfigBrand & { has_ml_rec_v2: boolean }

const Upsell = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [itemAdded, setItemAdded] = useState(false)
  const { title: siteTitle } = useAppSelector(selectBrand) || {}
  const { auth } = useAppSelector(selectCustomer)
  const quote = useAppSelector(selectQuote)
  const isCateringQuote = quote.order?.order_type === 'CATERING'
  const menuSlug = useAppSelector(selectMenuSlug)
  const cartIds = useAppSelector(selectCartIds)
  const cart = useAppSelector(selectCart)
  const { categories, soldOut } = useAppSelector(selectMenu)

  const upsellItems = useMemo(
    () => makeUpsellItemsForCart(categories, cartIds, soldOut) as CartItem[],
    [categories, cartIds, soldOut]
  )
  const items = useMemo(() => {
    return cart ? makeUpsellsAnalyticsItems(cart, upsellItems, 'CHECKOUT') : []
  }, [cart, upsellItems])

  const upsells = useAppSelector(selectContentSection('upsells'))
  const { show, title, subtitle, decline, proceed } = upsells?.checkout || {}
  const hasItems = upsellItems.length > 0
  const path = auth || isCateringQuote ? '/checkout' : '/checkout/guest'
  const skip = !show || !hasItems

  const checkout = () => navigate(path)

  // v2 upsell support | has_ml_rec_v2
  const order = useAppSelector(selectOrder)
  const { revenueCenter, serviceType } = order
  const config = useAppSelector(selectConfig)
  const brandConfig = config?.brand as ExtConfigBrand
  const hasMlRecV2 = brandConfig?.has_ml_rec_v2 || false
  const sessionId = window.localStorage.getItem('session_id') ?? ''
  const { recommendations } = useAppSelector(selectRecommendations)

  const menuItemsHash: { [i: number]: MenuItem } = useMemo(
    () => makeMenuItemLookup(categories),
    [categories]
  )
  const allMenuItems = useMemo(
    () => Object.values(menuItemsHash),
    [menuItemsHash]
  )

  const upsellItemsV2: CartItem[] = useMemo(
    () => {
      const upsellItemIds = recommendations?.upsell_items || []
      const items = allMenuItems.filter((item) => {
        const validIds = upsellItemIds.filter(
          (id) =>
            !cartIds.includes(id as number) && !soldOut.includes(id as number)
        )
        return validIds.includes(item.id)
      }).map(item => makeOrderItem(item)) as CartItem[]
      return cart ? makeUpsellsAnalyticsItems(cart, items, 'CART') : []
    },
    [allMenuItems, recommendations?.upsell_items, cartIds, soldOut, cart]
  )

  useEffect(() => {
    if (!skip) {
      sendUpsillsMenuView({ position: 'CHECKOUT', items })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip])

  useEffect(() => {
    if (skip) navigate(path)
  }, [skip, path, navigate])

  // Fetch recommendations once when the cart opens (on mount).
    // V1 recs are shown immediately; V2 recs swap in when the response arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      if (hasMlRecV2 && revenueCenter && serviceType && order?.cart?.length) {
        dispatch(
          fetchRecommendations({
            session_id: sessionId,
            revenue_center_id: revenueCenter.revenue_center_id,
            service_type: serviceType,
            device_type: 'WEB',
            cart: order.cart.map((item) => ({
              id: item.id,
              quantity: item.quantity,
            })),
          })
        )
      }
      return () => {
        dispatch(resetRecommendations())
      }
    }, [revenueCenter, serviceType, order?.cart, dispatch, hasMlRecV2, sessionId]
    ) // Fire once on mount (= once per cart open); cleanup resets on close

    // Use V2 recs only when the A/B test assigns variant 1 and items are available.
    // Otherwise fall back silently to V1 recs from menu state.
    const useV2Recs =
      hasMlRecV2 &&
      recommendations?.statsig_variant === 1 &&
      (recommendations.upsell_items?.length ?? 0) > 0

  if (skip) return null

  return (
    <>
      <Helmet>
        <title>Add-ons | {siteTitle}</title>
      </Helmet>
      <Content>
        <Header
          title={<HeaderLogo />}
          left={
            <Back
              label="Back to Menu"
              path={menuSlug}
              text={isMobile ? null : 'Back to Menu'}
            />
          }
          right={<Skip path={path} text="Skip" />}
        />
        <Main>
          <UpsellView>
            <UpsellHeader>
              <UpsellTitle>{title}</UpsellTitle>
              {subtitle && <UpsellSubtitle>{subtitle}</UpsellSubtitle>}
            </UpsellHeader>
            <UpsellItems
              upsellItems={(useV2Recs ? items : upsellItemsV2) as CartItem[]}
              isSmall={isMobile}
              addCallback={() => setItemAdded(true)}
            />
            <UpsellFooter>
              {itemAdded ? (
                <Button onClick={checkout}>{proceed}</Button>
              ) : (
                <ButtonLink onClick={checkout}>
                  {decline || 'No thanks'}
                </ButtonLink>
              )}
            </UpsellFooter>
          </UpsellView>
        </Main>
      </Content>
    </>
  )
}

export default Upsell
